import { useState, useCallback, useMemo, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Sidebar } from "./Sidebar";
import { ConversationList } from "./ConversationList";
import { ChatWindow } from "./ChatWindow";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useGetRoomsQuery } from "@/redux/feature/chat/chatApi";
import { useGlobalContext } from "@/context/GlobalContext";
import { TChatRoom } from "@/redux/feature/chat/chatType";
import { mapRoomsToConversations } from "@/redux/feature/chat/chatUtills";
import { useNavigate, useSearchParams } from "react-router-dom";

export function MessagingDashboard() {
  const { userData } = useGlobalContext();
  const myUserId = userData?.user_id!;
  const myRole = userData?.role as "client" | "creator";

  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data, isLoading } = useGetRoomsQuery([
    { name: "filter", value: filter },
    { name: "search", value: search },
  ]);
  const roomId = searchParams.get("room");

  const rooms = data?.data || [];
  const conversations = useMemo(
    () => mapRoomsToConversations(rooms, myUserId, myRole),
    [rooms, myUserId, myRole],
  );
  const [selectedConversation, setSelectedConversation] =
    useState<TChatRoom | null>(null);
  // const [messages, setMessages] = useState<Record<string, Message[]>>();

  const [isTyping, setIsTyping] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileView, setMobileView] = useState<"list" | "chat">("list");

  // const currentMessages = selectedConversation
  //   ? messages?.[selectedConversation._id] || []
  //   : [];

  const handleSelectConversation = (conversation: TChatRoom) => {
    setSelectedConversation(conversation);
    setMobileView("chat");
    const params = new URLSearchParams(searchParams);
    params.set("room", conversation.room_id);

    navigate({ search: params.toString() }, { replace: false });
  };

  const handleSendMessage = useCallback(
    (content: string) => {
      if (!selectedConversation) return;

      // Simulate typing response
      setIsTyping(true);
    },
    [selectedConversation],
  );

  const handleBackToList = () => {
    setMobileView("list");
  };

  useEffect(() => {
    if (roomId) {
      const activeConvo = conversations.find((item) => item.room_id === roomId);
      if (activeConvo) {
        setSelectedConversation(activeConvo);
      }
    }
  }, [roomId]);
  return (
    <div className="flex h-screen w-full bg-background overflow-hidden">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-card border-b border-border flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>
        <span className="font-semibold">Messages</span>
        <Avatar className="w-8 h-8">
          <AvatarImage src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face" />
          <AvatarFallback>KA</AvatarFallback>
        </Avatar>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarCollapsed && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-foreground/20 backdrop-blur-sm"
            onClick={() => setSidebarCollapsed(false)}
          />
          <div className="relative w-[240px] h-full animate-slide-in-left">
            <Sidebar />
            <button
              onClick={() => setSidebarCollapsed(false)}
              className="absolute top-4 right-4 p-2 hover:bg-muted rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Conversation List */}
      <div
        className={cn(
          "w-full md:w-[340px] flex-shrink-0 transition-all duration-300",
          "lg:block",
          mobileView === "list" ? "block" : "hidden md:block",
          "lg:mt-0 mt-14",
        )}
      >
        <ConversationList
          conversations={conversations}
          selectedId={selectedConversation?._id}
          onSelect={handleSelectConversation}
          className="h-full lg:h-screen"
        />
      </div>

      {/* Chat Window */}
      <div
        className={cn(
          "flex-1 transition-all duration-300",
          mobileView === "chat" ? "block" : "hidden md:block",
          "lg:mt-0 mt-14",
        )}
      >
        {/* Mobile back button */}
        {mobileView === "chat" && (
          <button
            onClick={handleBackToList}
            className="md:hidden fixed top-[72px] left-4 z-30 p-2 bg-card rounded-full shadow-soft-md"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        <ChatWindow
          contact={
            selectedConversation
              ? {
                  id: selectedConversation._id as string,
                  name: selectedConversation.name as string,
                  avatar: selectedConversation.avatar as string,
                  isOnline: selectedConversation.isOnline || false,
                }
              : null
          }
          isTyping={isTyping}
          onSendMessage={handleSendMessage}
          className="h-full lg:h-screen"
        />
      </div>

      {/* User Avatar - Top Right (Desktop) */}
      {/* <div className="hidden lg:block fixed top-4 right-6 z-50">
        <Avatar className="w-10 h-10 ring-2 ring-background shadow-soft-md cursor-pointer hover:ring-primary/20 transition-all duration-200">
          <AvatarImage src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face" />
          <AvatarFallback className="bg-primary/10 text-primary font-medium">
            KA
          </AvatarFallback>
        </Avatar>
      </div> */}
    </div>
  );
}
